/**
 * BGM 曲目表（音乐室用）：17 首全部为 tools/gen-bgm.mjs 程序化自产。
 * 顺序按游戏流程（东方 Music Room 惯例）：标题 → 各关道中/boss 配对 → Extra 道中/boss → 过关结尾曲（非循环）→ 制作名单曲。
 * comment 为每首的创作背景，与 gen-bgm.mjs 中 TRACKS 的设计一一对应。
 * 渲染：作曲事件 → MIDI → fluidsynth + GeneralUser GS soundfont（GM 音色），
 * 见 tools/soundfonts/README.md；VSCO 2 CE 采样引擎保留为回退（tools/samples/README.md）。
 */
export const MUSIC_TRACKS = [
  {
    key: 'bgm_title',
    title: '月下的开幕礼',
    subtitle: '标题画面主题曲',
    comment:
      '标题画面的曲子。全长约 70 秒。C 利底亚调式 96 拍，主旋律交给了马林巴，' +
      '像八音盒一样一颗一颗地响，底下是弦乐长音和低音提琴拨弦。' +
      '从第二循环开始，一支长笛带着新的对位旋律加入进来——月亮升起来之后，总得有人陪它说话。' +
      '写的时候满脑子是「今晚也要出发了」的感觉。',
  },
  {
    key: 'bgm_stage1',
    title: '云海散步道',
    subtitle: '第一关·云端主题曲',
    comment:
      '第一关的曲子。全长约 84 秒，正好陪你跑完一趟云海。D 大调 148，主题交给单簧管，' +
      '八分泵音拨弦贝斯带着经过音往前跑。曲式是变奏式发展：主题、对比、小高潮、再回到主题，' +
      '织体一段一段加密，像云一层一层厚起来。小高潮（约 52 秒起）把主题整个抬高一个八度，' +
      '是穿出云层忽然亮开的一瞬。全程节奏一致、无缝循环；过关的收束句交给了专门的结尾曲。' +
      '毕竟是第一关，不设防地明亮到底。',
  },
  {
    key: 'bgm_boss1',
    title: '风暴王的仪仗',
    subtitle: '云端 boss 主题曲',
    comment:
      '云端 boss 的曲子。全长约 126 秒，展开→高潮→变奏再现，高潮埋在 36 秒左右。' +
      '沿用第一关的 D 大调但提到 160，主角从单簧管换成小号——' +
      '同一座云宫，道中听的是散步曲，王座前吹的是 fanfare。别被排场吓到，弹幕可比这个凶多了。',
  },
  {
    key: 'bgm_stage2',
    title: '深蓝摇篮',
    subtitle: '第二关·深海主题曲',
    comment:
      '第二关·深海的曲子。全长约 90 秒。A 多利亚调式 96，通篇三连音，' +
      '摇摇晃晃像被水流推着走。主旋律是长笛——采样里自带的那点颤音，正好是水波的样子。' +
      '音域收窄了，深海里不需要大起大落。零星的钟琴声是从海面上漏下来的、很远的光。',
  },
  {
    key: 'bgm_boss2',
    title: '深渊的腕足',
    subtitle: '深海 boss 主题曲',
    comment:
      '深海 boss 的曲子。全长约 130 秒。三连音还在，但从摇篮变成了缠绕——克拉肯的腕足就是这种感觉吧。' +
      '主旋律交给了长号，从深水底部往上压，鼓换成沉重的三拍连打，高潮在 38 秒附近浮出海面。' +
      '这次的钟声听起来像沉船的钟。游泳圈请自备。',
  },
  {
    key: 'bgm_stage3',
    title: '齿轮心脏',
    subtitle: '第三关·机械主题曲',
    comment:
      '第三关·机械的曲子。全长约 87 秒。E 小调 165，核心是一条短促的切分 ostinato riff，' +
      '两三个音像齿轮一样反复咬合，小号踩着切分节奏往前冲，底鼓四踩、拨弦贝斯十六分。' +
      '高潮段 riff 整体抬高八度，流水线换挡提速。请小心不要被卷进去。',
  },
  {
    key: 'bgm_boss3',
    title: '熔炉核心过载',
    subtitle: '机械 boss 主题曲',
    comment:
      '机械 boss 的曲子。全长约 126 秒，175 仍是全曲库最快。' +
      '切分底鼓加拨弦贝斯的低音脉冲一路碾压，小号是短促的四音呼喊，喊完就走，绝不多留。' +
      '反拍断奏和双跳军鼓在后面追打，高潮约 38 秒处全线报警。建议在它冷却之前解决战斗。',
  },
  {
    key: 'bgm_stage4',
    title: '光苔之森',
    subtitle: '第四关·森林主题曲',
    comment:
      '第四关·森林的曲子。全长约 89 秒。G 利底亚调式 118，单簧管加附点节奏，' +
      '蹦蹦跳跳的，像光斑在苔上跳。贝斯是低音提琴拨弦的 walking，一路经过音，' +
      '和声用反拍断奏留出空隙给鸟叫（并没有真的录进去）。' +
      '森林里迷路才是正事，走太快会错过蘑菇。',
  },
  {
    key: 'bgm_boss4',
    title: '千年树人的怒号',
    subtitle: '森林 boss 主题曲',
    comment:
      '森林 boss 的曲子。全长约 128 秒。利底亚的明亮还在，但 142 的速度加反拍断奏，' +
      '硬是把「散步」变成了「被树根追杀」。单簧管踩着附点节奏，那是树人跺脚的脚步声，' +
      '钟琴像年轮一样一圈圈荡开。活了千年的东西发火，场面大概就是这样。',
  },
  {
    key: 'bgm_stage5',
    title: '幽影回廊',
    subtitle: '第五关·幽影主题曲',
    comment:
      '第五关·幽影的曲子。全长约 87 秒。D 弗里几亚调式的降二级一压下来，走廊就黑了。' +
      '速度只有 88，音符之间留了很多空白——在这座回廊里，空白比声音更可怕。' +
      '长笛贴着墙根走路，底鼓放慢成心跳，钟琴每隔一段才敲一下。请当作是为你敲的。',
  },
  {
    key: 'bgm_boss5',
    title: '虚无之眼的凝视',
    subtitle: '幽影 boss 主题曲',
    comment:
      '幽影 boss 的曲子。全长约 122 秒。D 弗里几亚继续压暗，速度从关卡的 88 提到 118——' +
      '那只眼睛盯过来的时候，心跳确实会快这么多。长笛的旋律稀疏得只剩注视，' +
      '底鼓和军鼓放得很沉，钟琴一下一下，像敲在太阳穴上。别盯着它看太久。',
  },
  {
    key: 'bgm_stage6',
    title: '星尘尽头',
    subtitle: '第六关·星界主题曲',
    comment:
      '第六关·星界的曲子。全长约 88 秒，终点前最后一段路。F# 小调 152，' +
      '小号吹着长线条的旋律一句一句往上爬，底下是加宽到四个音的弦乐 pad 和 walking 拨弦贝斯，' +
      '鼓放慢成半步感。小高潮来了两次，第二次更亮。抬头看看，星星都在为你让路。',
  },
  {
    key: 'bgm_boss6',
    title: '星尘核心崩解',
    subtitle: '星界 boss 主题曲',
    comment:
      '最终 boss 的曲子。全长约 131 秒。多主题轮换：A 段全员齐奏断音，像核心外壳一层层碎裂；' +
      'B 段换成高八度的流动旋律加宽幅弦乐 pad，八分对位像星图一样铺开。' +
      '168 的速度、双主题的排场，高潮约 40 秒处核心开始崩解。打到这里的人，请把这首当作饯别礼。',
  },
  {
    key: 'bgm_ex_stage',
    title: '回廊巡礼',
    subtitle: '额外关道中主题曲',
    comment:
      '额外关「终焉回廊」道中的曲子。全长约 82 秒，正好陪你走到王座门前。' +
      '与 boss 曲「决战时刻」同用 B 弗里几亚调式——同一座回廊，道中是走向王座的脚步，王座前才是决战。' +
      '速度压到 140（boss 曲 172），主旋律交给长笛蜿蜒而行，羽管键琴的十六分音像回廊深处不知疲倦的齿轮，' +
      '远处的钟声一下一下，数着你还剩多少路。高潮埋在 41 秒左右，然后留一点空白——门就要开了。',
  },
  {
    key: 'bgm_boss',
    title: '决战时刻',
    subtitle: '额外关 boss 主题曲',
    comment:
      '额外关「终焉回廊」的主题曲，全长约 128 秒，写给单关 10 符卡连战的长夜。' +
      'B 弗里几亚调式 172，小号加八分琶音，紧张感像公式一样叠满，展开→高潮→再现一座三连。' +
      '曾经是通用 boss 兜底曲，如今转正为 Extra 专用——没有续关的回廊里，它就是唯一的仪仗。',
  },
  {
    key: 'bgm_clear',
    title: '云上凯旋式',
    subtitle: '过关结算结尾曲',
    comment:
      '过关结算时播放的结尾曲，全长约 10 秒，不循环，播完即止。D 大调 200，' +
      '小号吹一句上行的 fanfare（I-IV-V-I 一路往上推），长音收束时弦乐对位和钟琴星点一起落下来。' +
      'loop 曲目全程不再放慢——收束这件事，交给这首专门的曲子来做。',
  },
  {
    key: 'bgm_credits',
    title: '谢幕后的星空',
    subtitle: '制作名单主题曲',
    comment:
      '制作名单场景的曲子，全长约 70 秒，为滚动的名字们而作。C 大调 96，' +
      '长笛吹着长线条的旋律，弦乐 pad 铺底，鼓只留一点心跳似的脉冲，钟琴远远地敲。' +
      '曲式走主题、对比、再回主题，结尾四句自然放慢收束——这首不作紧张 loop 用，慢一点没关系。' +
      '仗打完了，该坐下来看看星星，顺便看看都是谁陪你打了这一仗。',
  },
];
